<template>
  <section>
    <SubLinks />
    <div class="flex space-y-5 flex-col lg:flex-row lg:space-y-0">
      <SlotPanel id="table-list-view" class="h-full w-full lg:w-2/3">
        <TableListView
          :readOnly="false"
          :selectable="true"
          :tables="tables || []"
          :selectedTables="tablesToSelect"
          @updateCoordinates="updateCoordinates"
          @selectTable="onSelectTable"
          selectedTablesView
        />
      </SlotPanel>
      <SlotPanel id="floor-plan-sidebar" class="w-full lg:w-1/3 lg:ml-12">
        <ErrorDisplay v-bind="error" class="p-2" :class="error ? 'hidden' : 'block'" />
        <template v-if="currentFloorPlan">
          <template v-if="state == STATE.TABLE_SELECTED">
            <span class="font-heading font-semibold text-fg-primary absolute top-4">{{
              currentFloorPlan.name
            }}</span>
            <TableView
              :table="currentTable || {}"
              :isDefaultPrice="true"
              @closeView="selectTable(-1)"
              @deleteTable="deleteTable(currentTable)"
              @save="onSaveTable"
            />
          </template>
          <template v-else-if="state == STATE.ADD_TABLE">
            <TableView
              :isDefaultPrice="true"
              :table="nextTable"
              @closeView="selectTable(-1)"
              @save="onAddTable"
              class="mt-4"
              id="table-view-create"
            />
          </template>
          <template v-else-if="showFloorPlanDetail">
            <div class="flex">
              <div
                id="back-to-floor-plans"
                class="flex text-fg-link-primary cursor-pointer mb-4"
                @click="switchFloorPlan(-1)"
              >
                <ResArrowLeft class="mr-2 scale-125" />All Floor Plans
              </div>
            </div>
            <TextInput
              id="edit-input-floor-plan-name"
              label="Floor Plan Name"
              class="mb-1"
              v-model="currentFloorPlan.name"
            />
            <template v-if="showSaving">
              <div class="font-sans mt-7">Saving...</div>
            </template>
            <template v-else-if="showSaved">
              <div class="font-sans mt-7">Saved!</div>
            </template>
            <template v-else>
              <ButtonDefault
                label="Save"
                classes="mb-4 px-8"
                id="save-floor-plan-btn"
                @click="updateName"
              />
            </template>
            <label class="font-label text-fg-secondary uppercase text-xs block">Pricing</label>
            <div class="flex flex-wrap flex-row space-x-4 mb-5">
              <span
                v-for="(price, idx) in currentFloorPlanUniquePrices"
                :key="idx"
                class="cursor-pointer text-fg-link-primary price-filter"
                @click="setPriceToFilter(price)"
              >
                ${{ price }}
              </span>
            </div>
            <div>
              <ButtonDefault
                id="add-table-btn"
                class="mt-2 mr-4"
                label="Add Table"
                @click="addTableClicked"
                alt
              />
              <template v-if="hasStandingRoom">
                <ButtonDefault
                  id="add-standing-room-btn"
                  class="mt-2 mr-4"
                  label="Add Standing Room"
                  @click="addStandingRoomClicked"
                  alt
                />
              </template>
              <ButtonDefault
                id="duplicate-floor-plan-btn"
                class="mt-2 mr-4"
                label="Duplicate Floor Plan"
                @click="duplicateFloorPlan"
                alt
              />
              <ButtonDefault
                id="delete-floor-plan-btn"
                label="delete floor plan"
                @click="setState(STATE.DELETE_FLOOR_PLAN)"
                alt
              />
            </div>
          </template>
          <template v-else-if="state === STATE.BULK_TABLE_PRICES">
            <span class="font-heading font-semibold uppercase text-xl text-fg-primary block mb-4">{{
              currentFloorPlanName
            }}</span>
            <BulkTablePrices
              class="mt-4"
              :modelValue="priceToFilter"
              :uniquePrices="currentFloorPlanUniquePrices"
              :isUpdatingTablePricesInBulk="isUpdatingTablePricesInBulk"
              @updatePrice="onBulkTablePricesUpdatePrice"
              @cancel="onBulkTablePricesCancel"
              @update:modelValue="setPriceToFilter"
            />
          </template>
        </template>

        <template v-else
          ><!-- state == STATE.INITIAL -->
          <div class="flex justify-between">
            <div class="pr-4">
              <div
                v-for="floorPlan in floorPlans"
                :key="`floorPlan-${floorPlan.id}`"
                :id="`floorPlan-${floorPlan.id}-link`"
                class="font-sans text-fg-link-primary text-base mb-2 cursor-pointer hover:text-fg-primary"
                :class="{ 'text-fg-primary': routeFloorPlanId === floorPlan.id }"
                @click="switchFloorPlan(floorPlan.id)"
              >
                <span>{{ floorPlan.name }}</span>
                <div v-if="floorPlan.pricing.length > 0" class="text-sm">
                  {{ floorPlan.pricingStr }}
                </div>
              </div>
            </div>
            <div>
              <ButtonDefault
                id="add-new-floor-plan"
                label="Add"
                @click="setState(STATE.ADD_FLOOR_PLAN)"
                alt
              />
            </div>
          </div>
        </template>
      </SlotPanel>
    </div>
    <SlotModal
      :display="state == STATE.ADD_FLOOR_PLAN"
      id="add-new-floor-plan-modal"
      @close="setState(STATE.INITIAL)"
    >
      <template #header>Add New Floor Plan</template>
      <div class="mt-2 mb-4">
        <TextInput
          id="new-floor-plan-name"
          v-model="newFloorPlanModal.name"
          :placeholder="newFloorPlanModal.placeholder"
        />
      </div>
      <div class="mb-2 flex justify-between">
        <ButtonDefault label="Cancel" @click="setState(STATE.INITIAL)" alt />
        <ButtonDefault
          id="save-new-floor-plan-button"
          label="Save"
          @click="addFloorPlan"
          :disabled="!newFloorPlanModal.name"
        />
      </div>
    </SlotModal>
    <SlotModal
      :display="state == STATE.DUPLICATE_FLOOR_PLAN"
      id="duplicate-floor-plan"
      @close="setState(STATE.FLOOR_PLAN_SELECTED)"
    >
      <template #header>
        <template v-if="duplicateFloorPlanModal.floorPlan.id"> Duplication Complete </template>
        <template v-else> Duplicating </template>
      </template>
      <div class="mt-2 mb-4">
        <template v-if="duplicateFloorPlanModal.loading">
          <Spinner :display="true" />
        </template>
        <template v-else>
          <div>
            You can view the new Floor Plan,
            <span
              class="text-fg-link-primary cursor-pointer"
              @click="switchFloorPlan(duplicateFloorPlanModal.floorPlan.id)"
              >{{ duplicateFloorPlanModal.floorPlan.name }}</span
            >
          </div>
        </template>
      </div>
      <template v-if="duplicateFloorPlanModal.floorPlan.id">
        <div class="mb-2 flex justify-between">
          <ButtonDefault
            id="duplicate-modal-close"
            label="Close"
            @click="setState(STATE.FLOOR_PLAN_SELECTED)"
            alt
          />
          <ButtonDefault
            id="view-duplicate-floor-plan-button"
            label="view"
            @click="switchFloorPlan(duplicateFloorPlanModal.floorPlan.id)"
          />
        </div>
      </template>
    </SlotModal>
    <SlotModal
      :display="state == STATE.DELETE_FLOOR_PLAN"
      id="delete-floor-plan"
      @close="setState(STATE.FLOOR_PLAN_SELECTED)"
    >
      <template #header>Please Confirm</template>
      <div class="mt-2 mb-4">
        Are you sure you want to delete the
        <span class="font-semibold">{{ currentFloorPlanName }}</span> floor plan?
      </div>
      <div class="mb-2 flex justify-between">
        <ButtonDefault label="Cancel" @click="setState(STATE.FLOOR_PLAN_SELECTED)" alt />
        <ButtonDefault
          id="confirmed-delete-floor-plan-btn"
          label="delete"
          @click="deleteFloorPlan"
          altWarning
        />
      </div>
    </SlotModal>
  </section>
</template>

<script lang="ts">
import { mapStores } from 'pinia'
import { defineComponent } from 'vue'

import ResArrowLeft from '@/assets/resArrowLeft.svg?component'
import ButtonDefault from '@/common/components/atoms/ButtonDefault.vue'
import ErrorDisplay from '@/common/components/atoms/ErrorDisplay.vue'
import Spinner from '@/common/components/atoms/Spinner.vue'
import SubLinks from '@/common/components/molecules/SubLinks.vue'
import TextInput from '@/common/components/molecules/TextInput.vue'
import SlotModal from '@/common/components/organisms/SlotModal.vue'
import SlotPanel from '@/common/components/organisms/SlotPanel.vue'
import BulkTablePrices from '@/components/BulkTablePrices.vue'
import TableListView from '@/components/TableListView.vue'
import TableView from '@/components/TableView.vue'
import { StoreFloorPlan } from '@/models'
import { useFloorPlanStore } from '@/stores/floorplan'
import { useSettingStore } from '@/stores/setting'
import { blankError, clearError, displayHandled } from '@/utils/errors'
import { byId, nextName, sortBy } from '@/utils/utils'

import { FeatureFlag, TableRead, TableShape } from '@generated/types'

const STATE = {
  INITIAL: 'initial',
  FLOOR_PLAN_SELECTED: 'floor-plan-selected',
  TABLE_SELECTED: 'table-selected',
  ADD_TABLE: 'add-table',
  ADD_FLOOR_PLAN: 'add-floor-plan',
  DELETE_FLOOR_PLAN: 'delete-floor-plan',
  DUPLICATE_FLOOR_PLAN: 'duplicate-floor-plan',
  BULK_TABLE_PRICES: 'bulk-table-prices',
} as const

type State = (typeof STATE)[keyof typeof STATE]

export default defineComponent({
  name: 'FloorPlans',
  components: {
    BulkTablePrices,
    ButtonDefault,
    ErrorDisplay,
    ResArrowLeft,
    SlotModal,
    SlotPanel,
    Spinner,
    SubLinks,
    TableListView,
    TableView,
    TextInput,
  },
  data() {
    return {
      state: STATE.INITIAL as State,
      duplicateFloorPlanModal: {
        loading: false,
        floorPlan: {
          id: -1,
          name: '',
        },
      },
      deleteFloorPlanModal: {
        loading: false,
      },
      newFloorPlanModal: {
        name: '',
        placeholder: 'My New Floor Plan',
      },
      nextTable: {
        name: '',
        minimum_guests: 1,
        maximum_guests: 1,
        h: 2,
        w: 2,
        rotation: 0,
        shape: TableShape.SQUARE,
        default_price_per_person: 20,
        standing_room: false,
      },
      selectedTableId: -1,
      saveState: '' as 'SAVING' | 'SAVED' | '',
      error: blankError(),
      STATE,
      priceToFilter: undefined as number | undefined,
      selectedTablesIds: [] as number[],
      isUpdatingTablePricesInBulk: false,
    }
  },

  computed: {
    ...mapStores(useSettingStore, useFloorPlanStore),

    floorPlans(): StoreFloorPlan[] {
      sortBy(this.floorPlanStore.arrangements, 'name')
      return this.floorPlanStore.arrangements || []
    },

    currentFloorPlan(): StoreFloorPlan | null {
      return this.floorPlanStore.floorPlanById(this.routeFloorPlanId) || null
    },

    currentFloorPlanName(): string | null {
      return this.currentFloorPlan?.name || null
    },

    currentTable(): TableRead | null {
      return this.currentFloorPlan?.tables.find(byId(this.selectedTableId)) || null
    },

    hasStandingRoom(): boolean {
      return this.settingStore.flagEnabled(FeatureFlag.STANDING_ROOM)
    },

    tables(): TableRead[] {
      if (!this.currentFloorPlan) {
        return []
      }
      return this.currentFloorPlan.tables
    },

    activeTable(): TableRead | null {
      if (this.tables.length) {
        return this.currentTable
      }
      return null
    },

    routeFloorPlanId(): number {
      return Number(this.$route.params?.floorPlanId)
    },

    showFloorPlanDetail(): boolean {
      return this.state == STATE.FLOOR_PLAN_SELECTED || this.state == STATE.DELETE_FLOOR_PLAN
    },

    showSaving(): boolean {
      return this.saveState == 'SAVING'
    },

    showSaved(): boolean {
      return this.saveState == 'SAVED'
    },

    currentFloorPlanUniquePrices(): number[] {
      return (
        [
          ...new Set(
            this.currentFloorPlan?.tables.map(table => Number(table.default_price_per_person)),
          ),
        ].sort((a, b) => a - b) || []
      )
    },

    tablesToSelect(): number[] {
      if (this.selectedTableId !== -1) {
        return [this.selectedTableId]
      } else if (this.selectedTablesIds.length) {
        return this.selectedTablesIds
      } else {
        return []
      }
    },
  },

  async mounted() {
    await Promise.all([this.floorPlanStore.fetchAll(), this.settingStore.loading])
    if (!this.floorPlans.length) {
      await this.addFloorPlan()
    }
    if (this.routeFloorPlanId) {
      this.switchFloorPlan(this.routeFloorPlanId)
    }
  },

  methods: {
    setState(state: State) {
      this.state = state
    },

    async addFloorPlan() {
      const arrangement = await this.floorPlanStore.addFloorPlan(this.newFloorPlanModal.name)
      this.newFloorPlanModal.name = ''
      await this.switchFloorPlan(arrangement.id)
      return arrangement
    },

    async switchFloorPlan(floorPlanId) {
      if (floorPlanId > 0) {
        this.setState(STATE.FLOOR_PLAN_SELECTED)
        if (floorPlanId === this.routeFloorPlanId) return
        await this.$router.push({
          name: 'floorPlan',
          params: { floorPlanId: floorPlanId },
        })
      } else {
        this.setState(STATE.INITIAL)
        await this.$router.push({
          name: 'floorPlans',
        })
      }
    },

    addTableClicked() {
      this.nextTable.standing_room = false
      this.setState(STATE.ADD_TABLE)
    },

    addStandingRoomClicked() {
      this.nextTable.standing_room = true
      this.nextTable.minimum_guests = 0
      this.setState(STATE.ADD_TABLE)
    },

    async onAddTable(table) {
      clearError(this.error)

      try {
        const newTable = await this.floorPlanStore.upsertFloorPlanTable({
          id: this.routeFloorPlanId,
          table_id: 0,
          x: 2,
          y: 2,
          ...table,
        })
        this.selectTable(newTable.id)

        Object.assign(this.nextTable, table)
        this.nextTable.name = nextName(table.name)

        if (table.standing_room) {
          this.nextTable.h = 2
          this.nextTable.w = 2
        }
      } catch (error) {
        displayHandled(error, this.error)
      }
    },

    onSelectTable(tableIndex) {
      if (this.state === STATE.BULK_TABLE_PRICES) return
      const tableId = this.currentFloorPlan?.tables[tableIndex].id || -1
      this.selectTable(tableId)
    },

    selectTable(tableId) {
      this.selectedTableId = tableId
      if (tableId > 0) {
        this.setState(STATE.TABLE_SELECTED)
      } else {
        this.setState(STATE.FLOOR_PLAN_SELECTED)
      }
    },

    async deleteTable(table) {
      if (!this.currentFloorPlan) {
        return
      }

      await this.floorPlanStore.deleteFloorPlanTable({
        table_id: table.id,
        id: this.currentFloorPlan?.id,
      })

      this.selectTable(-1)
    },

    async onSaveTable({
      name,
      minimum_guests,
      maximum_guests,
      h,
      w,
      default_price_per_person,
      type_id,
      shape,
      rotation,
      held,
      standing_room,
    }) {
      if (!this.currentTable) {
        return
      }
      clearError(this.error)

      const table = this.currentTable
      try {
        await this.floorPlanStore.upsertFloorPlanTable({
          ...table,
          id: this.currentFloorPlan?.id,
          table_id: table.id,
          name,
          minimum_guests,
          maximum_guests,
          h,
          w,
          default_price_per_person,
          shape: shape || TableShape.SQUARE,
          rotation,
          type_id,
          held,
          standing_room,
        })
        this.selectTable(-1)
      } catch (error) {
        displayHandled(error, this.error)
      }
    },

    updateCoordinates({ x, y }) {
      if (!this.currentFloorPlan || !this.currentTable) {
        return
      }
      const table = this.currentTable

      if (table.x == x && table.y == y) {
        return
      }

      try {
        this.floorPlanStore.upsertFloorPlanTable({
          ...table,
          id: this.currentFloorPlan.id,
          table_id: table.id,
          name: table.name || '',
          shape: table.shape || TableShape.SQUARE,
          h: table.h || 2,
          w: table.w || 2,
          default_price_per_person: table.default_price_per_person || 0,
          x,
          y,
          rotation: table.rotation || 0,
          type_id: table.type_id,
          held: table.held || false,
        })
      } catch (error) {
        displayHandled(error, this.error)
      }
    },

    async deleteFloorPlan() {
      await this.floorPlanStore.deleteFloorPlan(this.routeFloorPlanId)
      this.switchFloorPlan(-1)
    },

    async duplicateFloorPlan() {
      if (!this.currentFloorPlan) {
        return
      }
      this.duplicateFloorPlanModal.loading = true
      this.setState(STATE.DUPLICATE_FLOOR_PLAN)
      const floorPlan = await this.floorPlanStore.duplicateFloorPlan(this.currentFloorPlan.id)
      this.duplicateFloorPlanModal.loading = false
      this.duplicateFloorPlanModal.floorPlan = floorPlan
    },

    async updateName() {
      if (!this.currentFloorPlan) {
        return
      }
      this.saveState = 'SAVING'
      await this.floorPlanStore.updateFloorPlan({
        id: this.currentFloorPlan.id,
        name: this.currentFloorPlan.name,
        is_primary: this.currentFloorPlan.is_primary,
      })
      this.saveState = 'SAVED'
      setTimeout(() => (this.saveState = ''), 2000)
    },

    onBulkTablePricesCancel(): void {
      this.setState(STATE.FLOOR_PLAN_SELECTED)
      this.selectedTablesIds = []
      this.priceToFilter = undefined
    },

    setPriceToFilter(price): void {
      this.priceToFilter = price
      this.setState(STATE.BULK_TABLE_PRICES)
      this.setSelectedTablesIdsByPriceToFilter()
    },

    setSelectedTablesIdsByPriceToFilter(): void {
      if (!this.currentFloorPlan) {
        return
      }
      const tables = this.currentFloorPlan.tables
      this.selectedTablesIds = tables
        .filter(table => Number(table.default_price_per_person) === this.priceToFilter)
        .map(table => table.id)
    },

    async onBulkTablePricesUpdatePrice(newPrice: number): Promise<void> {
      if (!this.currentFloorPlan) {
        return
      }
      this.isUpdatingTablePricesInBulk = true
      await this.floorPlanStore.bulkPriceFloorPlanTables(
        this.currentFloorPlan.id,
        this.selectedTablesIds,
        newPrice,
      )
      this.isUpdatingTablePricesInBulk = false
      this.onBulkTablePricesCancel()
    },
  },
})
</script>
